import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import { toast } from '@/components/ui/use-toast';
import { authLogger } from '@/lib/utils/secure-logger';
import { reportApiErrorToSentry } from '@/lib/utils/sentry-api-errors';
import { CLIENT_INSTANCE_HEADER, CLIENT_INSTANCE_ID } from '@/lib/clientInstanceId';

// Callers probing feature-gated endpoints (e.g. rewards when a store has it
// disabled) set this so an expected error response doesn't toast the user.
declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressErrorToast?: boolean;
    // Callers that handle failures themselves set this, so a failure never
    // reloads the page (maybeAutoRefresh).
    suppressAutoReload?: boolean;
  }
}

const PUBLIC_ROUTES = [
  '/api/login',
  // '/api/register', // disabled
  '/api/auth/login',
  // '/api/auth/register', // disabled
  '/api/settings',
  '/api/subscriptions',
  '/',
  '/login',
  '/register'
];
// Always use relative URLs. In production the app is served from the same origin as the
// API; in development Vite proxies /api to the backend (see vite.config.ts). Staying
// same-origin is what keeps the withCredentials auth cookie working regardless of whether
// you load the page via localhost, a LAN IP, or Tailscale - cookies are scoped by host,
// so an absolute API URL breaks the session the moment the page host differs.
// Set VITE_API_URL only if you deliberately need to point at a different backend.
const isProduction = import.meta.env.PROD;
const API_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // CSRF protection
    // Lets the server attribute a change back to the originating tab so
    // realtime handlers can skip their own echo (see lib/clientInstanceId).
    [CLIENT_INSTANCE_HEADER]: CLIENT_INSTANCE_ID,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache'
  },
  withCredentials: true,
  timeout: 30000, // 30 second timeout
  maxRedirects: 3
});

// Helper function to check if we're already on the login page
const isOnLoginPage = () => {
  return window.location.pathname === '/login';
};

// Track if we're in the initial authentication phase to suppress error toasts
let isInitialAuthPhase = true;
let authInitTimer: NodeJS.Timeout | null = null;

// Set a timer to disable the initial auth phase after 1 minute
const startAuthInitTimer = () => {
  if (authInitTimer) {
    clearTimeout(authInitTimer);
  }
  authInitTimer = setTimeout(() => {
    isInitialAuthPhase = false;
  }, 60000); // 1 minute
};

// Start the timer immediately when the module loads
startAuthInitTimer();

// Function to reset the auth phase (called during login)
export const resetAuthPhase = () => {
  isInitialAuthPhase = true;
  startAuthInitTimer();
};

// Retry configuration for transient network errors on idempotent requests
const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 500;
const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options']);
const RETRYABLE_STATUS_CODES = new Set([408, 429, 502, 503, 504]);

type RetryableRequestConfig = AxiosRequestConfig & {
  _apiErrorReported?: boolean;
  _retryCount?: number;
};

type RetryableAxiosError = AxiosError & {
  config?: RetryableRequestConfig;
};

function asRetryableError(error: any): RetryableAxiosError {
  return error as RetryableAxiosError;
}

function isIdempotentRequest(config?: RetryableRequestConfig): boolean {
  return IDEMPOTENT_METHODS.has((config?.method || 'get').toLowerCase());
}

function shouldRetryStatus(status?: number): boolean {
  return !!status && (RETRYABLE_STATUS_CODES.has(status) || status >= 500);
}

function isNotificationPollingRequest(config?: RetryableRequestConfig): boolean {
  const method = (config?.method || 'get').toLowerCase();
  const url = String(config?.url || '');
  return method === 'get' && (
    url === '/api/notifications' ||
    url.startsWith('/api/notifications?') ||
    url.includes('/api/notifications/push-status') ||
    url.includes('/api/notifications/notification-preference')
  );
}

function markApiErrorReported(config?: RetryableRequestConfig): boolean {
  if (!config) return true;
  if (config._apiErrorReported) return false;
  config._apiErrorReported = true;
  return true;
}

async function retryRequest(error: RetryableAxiosError): Promise<any> {
  const config = error.config;
  if (!config) return Promise.reject(error);

  const method = (config.method || 'get').toLowerCase();
  if (!IDEMPOTENT_METHODS.has(method)) return Promise.reject(error);

  config._retryCount = (config._retryCount ?? 0) + 1;
  if (config._retryCount > MAX_RETRIES) return Promise.reject(error);

  // Exponential back-off: 500 ms, then 1 000 ms
  const delay = RETRY_BASE_DELAY_MS * Math.pow(2, config._retryCount - 1);
  await new Promise<void>(resolve => setTimeout(resolve, delay));
  return api(config);
}

// In production, we auto-refresh the page once per 5 minutes on hard API failures
const AUTO_RELOAD_KEY_TS = 'autoReloadTs';
const AUTO_RELOAD_KEY_COUNT = 'autoReloadCount';

function maybeAutoRefresh(reason?: string) {
  if (!isProduction) return;
  try {
    if (isOnLoginPage()) return;
    const now = Date.now();
    const last = parseInt(sessionStorage.getItem(AUTO_RELOAD_KEY_TS) || '0');
    const count = parseInt(sessionStorage.getItem(AUTO_RELOAD_KEY_COUNT) || '0');
    const windowMs = 5 * 60 * 1000; // 5 minutes

    if (!last || (now - last) > windowMs) {
      sessionStorage.setItem(AUTO_RELOAD_KEY_TS, String(now));
      sessionStorage.setItem(AUTO_RELOAD_KEY_COUNT, '1');
    } else if (count >= 1) {
      return; // already auto-refreshed within the window
    } else {
      sessionStorage.setItem(AUTO_RELOAD_KEY_COUNT, String(count + 1));
    }

    authLogger.warn(`Auto-refreshing due to API failure${reason ? ` (${reason})` : ''}`);
    window.location.reload();
  } catch {
    // no-op
  }
}

// 🏢 ENTERPRISE: No request interceptor needed for httpOnly cookies
// Server automatically receives session cookie with each request
api.interceptors.request.use(async (config) => {
  // No token handling needed - httpOnly cookies are sent automatically
  return config;
}, (error) => {
  authLogger.error('Request error');
  return Promise.reject(error);
});

api.interceptors.response.use(
  (response) => {
    // Check if token was refreshed by server
    if (response.headers['x-token-refreshed']) {
      authLogger.debug('Server refreshed token automatically');
    }
    return response;
  },
  async (error) => {
    // Don't show error toasts during initial authentication phase
    const shouldSuppressToast = isInitialAuthPhase;

    // Handle 401 Unauthorized errors
    const isPublicRoute = PUBLIC_ROUTES.some(route => error.config?.url?.includes(route));

    if (error.response?.status === 401) {
      // Suppress error logging for expected 401s during initial auth phase
      // This prevents console errors from causing page crashes in headless test mode
      const isExpectedAuthError = isInitialAuthPhase;

      if (!isPublicRoute && !isOnLoginPage()) {
        // Check if this might be a network timeout rather than an authentication error
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
          authLogger.warn('Network error during authentication check, not redirecting to login');
          return Promise.reject(error);
        }

        // Try to handle auth error with auth service (attempt silent refresh once)
        const authService = await import('@/services/authService');
        const handled = authService.default.handleAuthError(error);

        if (handled) {
          // Auth service is handling this error (either refreshing or dispatching auth required)
          // Don't show toast or redirect here
          return Promise.reject(error);
        }

        // Final fallback: do not force-clear or redirect immediately.
        // Allow the page to handle auth:required events or manual navigation.
        authLogger.auth('Authentication required - awaiting user action');
        return Promise.reject(error);
      } else if (!isOnLoginPage() && !shouldSuppressToast) {
        // In production, do not show toasts for auth errors; attempt a graceful recovery
        if (isProduction) {
          maybeAutoRefresh('401');
        } else {
          const errorMessage = error.response?.data?.message || 'Invalid credentials';
          if (!errorMessage.toLowerCase().includes('token') &&
              !errorMessage.toLowerCase().includes('session')) {
            toast({
              title: 'Authentication Error',
              description: errorMessage,
              variant: 'destructive',
            });
          }
        }
      }

      // For expected auth errors on login page, suppress the error to prevent console logging
      if (isExpectedAuthError) {
        // Return a resolved promise to prevent error logging
        return Promise.resolve({ data: null });
      }
    } else if (error.response && !shouldSuppressToast) {
      // Non-auth errors: in production, avoid spamming users and auto-refresh once
      if (isProduction) {
        const status = error.response.status;
        if (status === 429 || status >= 500) {
          const isPassiveNotificationPoll = isNotificationPollingRequest(error.config);

          if (shouldRetryStatus(status) && isIdempotentRequest(error.config)) {
            try {
              return await retryRequest(error);
            } catch (retryError: any) {
              if (isPassiveNotificationPoll && document.visibilityState === 'hidden') {
                return Promise.reject(retryError);
              }

              if (markApiErrorReported(error.config)) {
                reportApiErrorToSentry({
                  url: error.config?.url,
                  method: error.config?.method?.toUpperCase(),
                  status: error.response?.status || status,
                  message: error.response?.data?.message || error.message,
                  severity: isPassiveNotificationPoll ? 'warning' : undefined,
                }, {
                  retried: true,
                  retry_count: error.config?._retryCount,
                  passive_notification_poll: isPassiveNotificationPoll,
                });

                if (!isPassiveNotificationPoll && !error.config?.suppressAutoReload) {
                  maybeAutoRefresh(String(status));
                }
              }

              return Promise.reject(retryError);
            }
          }

          if (!(isPassiveNotificationPoll && document.visibilityState === 'hidden')) {
            reportApiErrorToSentry({
              url: error.config?.url,
              method: error.config?.method?.toUpperCase(),
              status,
              message: error.response.data?.message || error.message,
              severity: isPassiveNotificationPoll ? 'warning' : undefined,
            }, {
              passive_notification_poll: isPassiveNotificationPoll,
            });
          }

          if (!isPassiveNotificationPoll && !error.config?.suppressAutoReload) {
            maybeAutoRefresh(String(status));
          }
        }
        // No toast in production
      } else if (!error.config?.suppressErrorToast) {
        const errorMessage = error.response.data?.message || 'An unexpected error occurred';
        if (!errorMessage.toLowerCase().includes('token') &&
            !errorMessage.toLowerCase().includes('session expired')) {
          toast({
            title: 'Error',
            description: errorMessage,
            variant: 'destructive',
          });
        }
      }
    } else if (!error.response) {
      // Network error or timeout — retry idempotent requests before reporting
      const isRetryable = error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';

      // 🕒 OPTIMIZATION: If the tab is hidden, skip retry and Sentry reporting for timeouts.
      // Browsers stall network when hidden; background timeouts are expected and noisy.
      if (isRetryable && document.visibilityState === 'hidden') {
        return Promise.reject(error);
      }

      if (isRetryable) {
        try {
          return await retryRequest(error);
        } catch (retryError: any) {
          const finalError = asRetryableError(retryError);
          const isPassiveNotificationPoll = isNotificationPollingRequest(finalError.config);
          if (
            isProduction &&
            document.visibilityState !== 'hidden' &&
            markApiErrorReported(finalError.config)
          ) {
            reportApiErrorToSentry({
              url: finalError.config?.url,
              method: finalError.config?.method?.toUpperCase(),
              code: finalError.code,
              message: finalError.message,
              severity: isPassiveNotificationPoll ? 'warning' : undefined,
            }, {
              retried: true,
              retry_count: finalError.config?._retryCount,
              passive_notification_poll: isPassiveNotificationPoll,
            });

              if (!isPassiveNotificationPoll && !finalError.config?.suppressAutoReload) {
              maybeAutoRefresh(finalError.code || 'network');
              }
          }
          return Promise.reject(retryError);
        }
      }
      // Non-retryable network failure (POST/PUT etc. with no response)
      if (isProduction && document.visibilityState !== 'hidden') {
        reportApiErrorToSentry({
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          code: error.code,
          message: error.message,
        });
        if (!error.config?.suppressAutoReload) {
          maybeAutoRefresh(error.code || 'network');
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;